import React, { useState } from 'react';
import { FaFileCode } from 'react-icons/fa';
import ConfirmationPopup from '../Utils/ConfirmationPopup';
import ChallengeQuestionList from './ChallengeQuestionList';
import AddChallengeQuestion from './Shards/Questions/AddChallengeQuestion';
import AddFromLibrary from '../LibraryComponents/AddFromLibrary';
import CriteriaView from './Shards/CriteriaView';
import ImprovementsView from './Shards/ImprovementsView';
import RawJsonEditor from '../Utils/RawJsonEditor';
import { useSession } from '../Utils/SessionContext';
import { updateChallengeQuestions } from './Utils/QuestionUpdater';
import toast from 'react-hot-toast';

/**
 * EvaluationChallenge Component
 * Displays a challenge with questions and answers for evaluations
 * 
 * @param {Object} challenge - The challenge data to display
 * @param {string} evaluationType - The type of evaluation (e.g., 'job', 'talent')
 * @param {boolean} showHeader - Whether to show the header section (default: true)
 */
const EvaluationChallenge = ({ challenge, evaluationType, showHeader = true }) => {
  const { user } = useSession();
  
  // Extract filtered hardSkills based on evaluation type
  const getFilteredSkills = () => {
    if (!challenge?.challenge_criteria_json?.hardSkills?.items) {
      return [];
    }
    
    const allSkills = challenge.challenge_criteria_json.hardSkills.items;
    
    if (evaluationType === 'VSJob') {
      // For VSJob: return only skills with importance="Required"
      return allSkills.filter(skill => skill.importance === 'Required');
    } else if (evaluationType === 'VSResume') {
      // For VSResume: return only skills with importance="Primary"
      return allSkills.filter(skill => skill.importance === 'Primary');
    } else if (evaluationType === 'VSSkillset') {
      // For VSSkillset: return all skills
      return allSkills;
    } else {
      // For any other type: return all skills
      return allSkills;
    }
  };
  
  const [confirmPopup, setConfirmPopup] = useState({ 
    show: false, 
    questionId: null,
    isFullChallenge: false 
  });
  const [showCriteriaPopup, setShowCriteriaPopup] = useState(false);
  const [showImprovementsPopup, setShowImprovementsPopup] = useState(false);
  const [isRawEditOpen, setIsRawEditOpen] = useState(false);
  const [isAddQuestionOpen, setIsAddQuestionOpen] = useState(false);
  const [isAddFromLibraryOpen, setIsAddFromLibraryOpen] = useState(false);
  
  const handleRegenerateQuestion = (questionId) => {
    alert('Coming Soon! Question regeneration will be available shortly.');
  };
  
  const handleRegenerateFullChallenge = () => {
    setConfirmPopup({ show: true, questionId: null, isFullChallenge: true });
  };
  
  const confirmRegeneration = () => {
    if (confirmPopup.isFullChallenge) {
      // Placeholder for regenerating the entire challenge
      console.log('Regenerating entire challenge');
      // This would typically call an API to regenerate the challenge
    } else {
      // Placeholder for regenerating a specific question
      console.log(`Regenerating question with ID: ${confirmPopup.questionId}`);
      // This would typically call an API to regenerate the specific question
    }
    setConfirmPopup({ show: false, questionId: null, isFullChallenge: false });
  };
  
  const cancelRegeneration = () => {
    setConfirmPopup({ show: false, questionId: null, isFullChallenge: false });
  };

  // Save function for challenge JSON using utility function
  const handleSaveChallenge = async (editedData) => {
    if (!challenge?.challenge_id) {
      throw new Error('Challenge ID is required to save challenge');
    }

    try {
      console.log('Saving edited challenge JSON:', editedData);

      // Use utility function to update questions
      await updateChallengeQuestions(
        challenge.challenge_id,
        editedData.questions,
        user
      );

      toast.success('Challenge updated successfully!');
      
      // Reload the page to refresh data
      window.location.reload();
    } catch (error) {
      console.error('Error updating challenge:', error);
      toast.error('Failed to update challenge. Please try again.');
      throw error; // Re-throw so RawJsonEditor can handle it
    }
  };
  
  if (!challenge?.challenge_json?.questions?.length) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600">No challenge data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Confirmation Popup */}
      <ConfirmationPopup 
        show={confirmPopup.show}
        title="Confirm Regeneration"
        message={confirmPopup.isFullChallenge 
          ? "Are you sure you want to regenerate and replace the entire challenge?" 
          : "Are you sure you want to regenerate and replace this question?"}
        onConfirm={confirmRegeneration}
        onCancel={cancelRegeneration}
      />
      
      {/* Optional Header */}
      {showHeader && (
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="mr-8">
              <h2 className="text-2xl font-bold text-gray-900">Challenge</h2>
              <p className="text-gray-600 mt-2">
                These are the scored challenge questions the candidate has to solve.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white border border-gray-300 text-gray-700">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Approx. Time:&nbsp;<span className="font-bold">{challenge.max_duration_minutes} min</span>
                </span>
                {challenge.challenge_type && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white border border-gray-300 text-gray-700">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Challenge Type:&nbsp;<span className="font-bold">{challenge.challenge_type}</span>
                  </span>
                )}
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white border border-gray-300 text-gray-700">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Seniority:&nbsp;<span className="font-bold">{challenge.seniority || 'N/A'}</span>
                </span>
              </div>
              
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              {/* Add Question Dropdown */}
              <div className="relative group">
                <button 
                  className="h-10 px-4 text-white rounded hover:opacity-90 transition-colors flex items-center whitespace-nowrap"
                  style={{ backgroundColor: 'var(--color-brand-green)' }}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Question</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="py-1">
                    {/* Add Own Question */}
                    <button
                      onClick={() => setIsAddQuestionOpen(true)}
                      className="w-full text-left px-4 py-3 hover:opacity-90 transition-colors flex items-center"
                      style={{ backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-brand-blue-light)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div className="w-8 h-8 bg-white border border-gray-300 rounded flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Add Own Question</div>
                        <div className="text-xs text-gray-500">Create a custom question</div>
                      </div>
                    </button>
                    
                    {/* Add From Expert Library */}
                    <button
                      onClick={() => setIsAddFromLibraryOpen(true)}
                      className="w-full text-left px-4 py-3 hover:opacity-90 transition-colors flex items-center"
                      style={{ backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-brand-purple-light)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div className="w-8 h-8 bg-white border border-gray-300 rounded flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Add From Expert Library</div>
                        <div className="text-xs text-gray-500">Browse pre-made questions</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Actions Dropdown */}
              <div className="relative group">
                <button className="h-10 px-4 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:border-gray-400 transition-colors flex items-center whitespace-nowrap">
                  <span>Actions</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="py-1">
                    {/* View Criteria */}
                    <button
                      onClick={() => setShowCriteriaPopup(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 616 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      View Criteria
                    </button>
                    
                    {/* Raw JSON Edit - only visible for Superadmin */}
                    {user?.role === 'Superadmin' && (
                      <>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => setIsRawEditOpen(true)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                        >
                          <FaFileCode className="w-4 h-4 mr-2" />
                          Raw JSON Edit
                        </button>
                        
                        {/* Regenerate Challenge */}
                        <button
                          onClick={handleRegenerateFullChallenge}
                          disabled
                          className="w-full text-left px-4 py-2 text-sm text-gray-400 cursor-not-allowed flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Regenerate Challenge
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Question Modal */}
      <AddChallengeQuestion
        isOpen={isAddQuestionOpen}
        onClose={() => setIsAddQuestionOpen(false)}
        challengeId={challenge.challenge_id}
        evaluationType={evaluationType}
        roleCategory={challenge.challenge_role_category}
        allQuestions={challenge.challenge_json.questions}
        onSuccess={() => window.location.reload()}
      />

      {/* Add from Library Modal */}
      <AddFromLibrary
        isOpen={isAddFromLibraryOpen}
        onClose={() => setIsAddFromLibraryOpen(false)}
        challengeId={challenge.challenge_id}
        currentQuestions={challenge.challenge_json.questions}
        onQuestionsAdded={() => window.location.reload()}
        prefilledSkills={getFilteredSkills()}
      />

      {/* Questions */}
      <ChallengeQuestionList 
        questions={challenge.challenge_json.questions} 
        onRegenerateQuestion={handleRegenerateQuestion}
        challengeId={challenge.challenge_id}
        onQuestionUpdated={() => window.location.reload()}
      />
      
      {/* Criteria View Popup */}
      <CriteriaView
        show={showCriteriaPopup}
        onClose={() => setShowCriteriaPopup(false)}
        criteria_json={challenge?.challenge_criteria_json}
      />
      
      {/* Improvements View Popup */}
      {/* <ImprovementsView
        show={showImprovementsPopup}
        onClose={() => setShowImprovementsPopup(false)}
        questions={challenge?.challenge_json?.questions || []}
      /> */}
      
      {/* Raw Edit Challenge Modal */}
      <RawJsonEditor
        isOpen={isRawEditOpen}
        onClose={() => setIsRawEditOpen(false)}
        data={challenge?.challenge_json}
        title="Raw Edit Challenge"
        description="Edit the challenge JSON directly. This is a temporary solution which will be replaced with a form."
        rootName="challenge"
        hiddenAttributes={[]}
        onSave={handleSaveChallenge}
        saveButtonText="Save"
      />
    </div>
  );
};

export default EvaluationChallenge;
